import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const WizardStepper = ({ currentStep, totalSteps, onStepClick }) => {
  const intl = useIntl();
  
  const stepNames = [
    intl.formatMessage({ id: 'succession_deals.wizard.stepper.steps.deal_type' }),
    intl.formatMessage({ id: 'succession_deals.wizard.stepper.steps.deal_information' }),
    intl.formatMessage({ id: 'succession_deals.wizard.stepper.steps.match_preferences' }),
    intl.formatMessage({ id: 'succession_deals.wizard.stepper.steps.deal_questions' }),
    intl.formatMessage({ id: 'succession_deals.wizard.stepper.steps.review_publish' })
  ];

  const steps = stepNames.map((name, idx) => ({
    id: `step-${idx + 1}`,
    label: name
  }));

  // currentStep is 1-indexed in the wizard, but we need 0-indexed for the progress calculation
  const zeroIndexedCurrentStep = currentStep - 1;

  return (
    <div className="relative" style={{ minHeight: '90px' }}>
      {/* Connecting Line Background */}
      <div 
        className="absolute bg-succession-border" 
        style={{ 
          top: '28px', 
          height: '2px',
          left: `calc(${100 / totalSteps / 2}%)`,
          right: `calc(${100 / totalSteps / 2}%)`
        }}
      >
        {/* Progress Line */}
        <div
          className="bg-succession-primary transition-all duration-300"
          style={{
            height: '2px',
            width: `${(zeroIndexedCurrentStep / (totalSteps - 1)) * 100}%`,
          }}
        />
      </div>

      {/* Steps */}
      <div className="relative flex justify-between" style={{ paddingBottom: '20px' }}>
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const isClickable = stepNumber <= currentStep;

          return (
            <div
              key={step.id}
              className="flex flex-col items-center"
              style={{ width: `${100 / totalSteps}%` }}
            >
              <button
                onClick={() => isClickable ? onStepClick(stepNumber) : null}
                disabled={!isClickable}
                className={`relative w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all duration-200 mb-2 ${
                  isCompleted
                    ? 'bg-succession-primary border-succession-primary text-white'
                    : isCurrent
                    ? 'bg-succession-bg-primary border-succession-primary text-succession-primary ring-4 ring-succession-primary'
                    : 'bg-succession-bg-primary border-succession-border text-succession-text-muted'
                } ${isClickable ? 'cursor-pointer hover:scale-110' : 'cursor-not-allowed'}`}
                style={{ zIndex: 10 }}
              >
                {isCompleted ? (
                  // Checkmark icon for completed steps
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <span className="text-base font-semibold">{stepNumber}</span>
                )}
              </button>

              <div className="text-center px-2">
                <p
                  className={`text-xs font-semibold uppercase tracking-wide transition-colors ${
                    isCurrent
                      ? 'text-succession-text-primary'
                      : isCompleted
                      ? 'text-succession-text-primary'
                      : 'text-succession-text-muted'
                  }`}
                >
                  {step.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

WizardStepper.propTypes = {
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  onStepClick: PropTypes.func.isRequired
};

export default WizardStepper; 